import { useEffect, useState } from "react";
import { CalendarCheck, FileText, GraduationCap, Link2, Music4 } from "lucide-react";
import { childApi, API_BASE } from "../lib/api";

const fileUrl = (fid) => `${API_BASE}/content/file/${fid}`;

function TaskCard({ it, onToggle }) {
  const overdue = it.due_date && !it.my_done && new Date(it.due_date) < new Date(new Date().toDateString());
  return (
    <div className="rounded-2xl bg-white border-2 border-[#C69B3D]/40 p-4 sm:p-5 flex items-start gap-3" data-testid={`choir-task-${it.id}`}>
      <button onClick={() => onToggle(it)} data-testid={`choir-task-toggle-${it.id}`} aria-pressed={!!it.my_done}
        aria-label={it.my_done ? `Mark "${it.title}" as not done` : `Mark "${it.title}" as done`}
        className={`mt-0.5 w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${it.my_done ? "bg-[#2D6A4F] border-[#2D6A4F] text-white" : "border-[#C69B3D] text-transparent hover:bg-[#C69B3D]/10"}`}>
        ✓
      </button>
      <div className="min-w-0">
        <p className={`font-bold text-[#0B192C] ${it.my_done ? "line-through opacity-60" : ""}`}>{it.title}</p>
        {it.body && <p className="text-sm text-[#566270] mt-1 whitespace-pre-wrap">{it.body}</p>}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {it.due_date && (
            <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${overdue ? "bg-[#A62B2B]/10 text-[#A62B2B]" : "bg-[#F3EFE0] text-[#566270]"}`}
              data-testid={`choir-task-due-${it.id}`}>
              {overdue ? "Overdue — was due " : "Due "}{new Date(it.due_date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
            </span>
          )}
          {it.file_id && (
            <a href={fileUrl(it.file_id)} target="_blank" rel="noreferrer" data-testid={`choir-task-file-${it.id}`}
              className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full bg-[#0B192C] text-[#FAF7EE] min-h-[28px]">
              <FileText className="w-3 h-3" aria-hidden /> Notes
            </a>
          )}
        </div>
        {it.my_feedback?.length > 0 && (
          <div className="mt-2 space-y-1.5" data-testid={`choir-task-feedback-${it.id}`}>
            {it.my_feedback.map((f, i) => (
              <p key={i} className="text-xs bg-[#F3EFE0] rounded-xl px-3 py-2 text-[#1C242B]">
                <span className="font-bold">{f.staff_name}:</span> {f.note}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RepertoireCard({ it }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E5DEC9] p-4 sm:p-5" data-testid={`choir-rep-${it.id}`}>
      <p className="font-bold text-[#0B192C]">{it.title}{it.composer ? <span className="font-normal text-[#566270]"> — {it.composer}</span> : null}</p>
      {it.body && <p className="text-sm text-[#566270] mt-1 whitespace-pre-wrap">{it.body}</p>}
      {it.file_id && (
        <audio controls preload="none" src={fileUrl(it.file_id)} className="w-full mt-3" data-testid={`choir-rep-audio-${it.id}`} />
      )}
      {it.external_url && (
        <a href={it.external_url} target="_blank" rel="noreferrer" data-testid={`choir-rep-link-${it.id}`}
          className="inline-flex items-center gap-1 text-sm font-semibold text-[#7A1C2E] mt-2 min-h-[44px]">
          <Link2 className="w-3.5 h-3.5" aria-hidden /> Open link
        </a>
      )}
    </div>
  );
}

function SimpleCard({ it, icon: Icon }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl bg-white border border-[#E5DEC9] p-4 sm:p-5" data-testid={`choir-item-${it.id}`}>
      <button onClick={() => setOpen(!open)} data-testid={`choir-item-toggle-${it.id}`} aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 text-left min-h-[44px]">
        <span className="flex items-center gap-2 font-bold text-[#0B192C] min-w-0">
          <Icon className="w-4 h-4 text-[#C69B3D] shrink-0" aria-hidden /> <span className="truncate">{it.title}</span>
        </span>
        <span className="text-xs font-bold text-[#566270] shrink-0">{open ? "Close" : "Open"}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          {it.body && <p className="text-sm text-[#1C242B] whitespace-pre-wrap">{it.body}</p>}
          {it.file_id && (
            <a href={fileUrl(it.file_id)} target="_blank" rel="noreferrer" data-testid={`choir-item-file-${it.id}`}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#0B192C] text-[#FAF7EE] text-sm font-bold min-h-[44px]">
              <FileText className="w-4 h-4" aria-hidden /> Open file
            </a>
          )}
          {it.external_url && (
            <a href={it.external_url} target="_blank" rel="noreferrer" data-testid={`choir-item-link-${it.id}`}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-[#0B192C] text-[#0B192C] text-sm font-bold min-h-[44px]">
              <Link2 className="w-4 h-4" aria-hidden /> Open link
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function ChoirContent() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    childApi.get("/child/content").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  if (!items || items.length === 0) return null;

  const toggle = async (it) => {
    setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, my_done: !x.my_done } : x)));
    try { await childApi.post(`/child/content/${it.id}/complete`); }
    catch { setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, my_done: it.my_done } : x))); }
  };

  const tasks = items.filter((i) => i.kind === "task");
  const rep = items.filter((i) => i.kind === "repertoire");
  const rest = items.filter((i) => i.kind === "resource" || i.kind === "lesson");

  return (
    <section className="space-y-3" data-testid="choir-content-section">
      <h2 className="font-display text-lg font-bold text-[#0B192C] flex items-center gap-2">
        <Music4 className="w-5 h-5 text-[#C69B3D]" aria-hidden /> From your choir
      </h2>
      {tasks.map((it) => <TaskCard key={it.id} it={it} onToggle={toggle} />)}
      {rep.map((it) => <RepertoireCard key={it.id} it={it} />)}
      {rest.map((it) => <SimpleCard key={it.id} it={it} icon={it.kind === "lesson" ? GraduationCap : FileText} />)}
      <div className="flex items-center gap-2 text-xs text-[#566270]">
        <CalendarCheck className="w-3.5 h-3.5" aria-hidden /> Set by your choir staff — just for {items[0] ? "your choir" : "you"}.
      </div>
    </section>
  );
}
